import { effectiveTerminals } from "../effective";
import type { DefLookup } from "../catalog";
import type { Issue, System } from "../types";
import { buildGraph, multiSourceBfs, pathCables, v, type Vertex } from "./graph";

/** A conductor path across one battery/cell is a short, even when the path
 * passes through a fuse or a monitor's literal terminal. Do not remove sense
 * cables or bridge distinct monitor inputs, loads, converters or batteries.
 * Group cells sharing the same shorted rail so a mistaken balance harness
 * produces one actionable finding rather than one card per cell. */
export function detectBatteryShortCircuits(system: System, defLookup: DefLookup): Issue[] {
  // A nonexistent terminal is a separate dangling-cable finding, not proof
  // of a physical connection. Only present posts can complete this short.
  const terminals = new Set<string>();
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (def) for (const terminal of effectiveTerminals(def, node)) terminals.add(v(node.id, terminal.id));
  }
  const cables = system.cables.filter(c => terminals.has(v(c.fromNodeId, c.fromTerminalId)) && terminals.has(v(c.toNodeId, c.toTerminalId)));
  const graph = buildGraph({ ...system, cables }, defLookup);
  const railByVertex = new Map<Vertex, Vertex>();
  const groups = new Map<Vertex, { nodeIds: Set<string>; cableIds: Set<string> }>();
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (def?.category !== "battery") continue;
    const positives = def.terminals.filter(t => t.polarity === "positive");
    const negatives = def.terminals.filter(t => t.polarity === "negative" || t.polarity === "ground");
    for (const positive of positives) {
      const seed = v(node.id, positive.id);
      const { dist, prev } = multiSourceBfs(graph, [seed]);
      const rail = railByVertex.get(seed) ?? seed;
      for (const vertex of dist.keys()) railByVertex.set(vertex, rail);
      for (const negative of negatives) {
        const target = v(node.id, negative.id);
        if (!dist.has(target)) continue;
        const group = groups.get(rail) ?? { nodeIds: new Set<string>(), cableIds: new Set<string>() };
        group.nodeIds.add(node.id);
        for (const cableId of pathCables(graph, target, prev)) group.cableIds.add(cableId);
        groups.set(rail, group);
      }
    }
  }
  return [...groups.values()].map(group => ({
    code: "battery-short-circuit",
    severity: "critical",
    title: { key: "diag.battery-short-circuit.title" },
    message: { key: "diag.battery-short-circuit.message", vars: { count: group.nodeIds.size } },
    fix: { key: "diag.battery-short-circuit.fix" },
    nodeIds: [...group.nodeIds],
    cableIds: [...group.cableIds],
  }));
}
